import { Injectable, signal, inject } from '@angular/core';
import { CartItem } from '../models/cart-item.model';
import { Address } from '../models/address.model';
import { Order, OrderStatus, OrderType } from '../models/order.model';
import { OrderApiService } from '../core/api/order-api.service';
import { TrackingJwtService } from './tracking-jwt-service';
import { reservationCoverFoodCreditInr } from '../constants/reservation-food-credit';
import { JourneyAnalyticsService } from '../core/analytics/journey-analytics.service';

@Injectable({
  providedIn: 'root',
})
export class CartService {
  orderSignal = signal<Order | null>(null);
  // TODO eliminate the cartItemsSignal and derive cart items from the order signal to avoid duplication of state and potential inconsistencies. The order signal can be the single source of truth for the cart/order state.
  cartItemsSignal = signal<CartItem[]>([]);
  deliveryAddressSelectedSignal = signal<boolean>(false);
  private savedDeliveryAddresses: Address[] = [];
  private selectedDeliveryAddress: Address | null = null;
  // UI state: whether the address dialog/modal is open (helps hide floating UI)
  isAddressDialogOpen = signal(false);
  private additionalDiscount: number = 0;
  private deliveryFee: number = 0;
  private otherCharges: number = 0;
  private gstRate: number = 0.05;
  selectedOrderType = signal<OrderType>('Delivery');
  tableNoSignal = signal(0);
  reservationNumberSignal = signal<number | null>(null);
  /** Mongo reservation id when ordering in a booking context (pre-order or dine-in). */
  reservationMongoIdSignal = signal<string | null>(null);
  /** Party size for the active reservation context (dine-in cover credit). */
  reservationPartySizeSignal = signal<number | null>(null);
  /** Remaining reservation cover credit for the active table session (from QR / table landing). */
  tableSessionRemainingCoverInr = signal<number | null>(null);

  /** Payable totals sent to the API and stored server-side use at most 2 decimal places. */
  private roundPayableAmount(amount: number): number {
    return Math.round(amount * 100) / 100;
  }

  private orderApi = inject(OrderApiService);
  private trackingJwtService = inject(TrackingJwtService);
  private journeyAnalytics = inject(JourneyAnalyticsService);
  
  addToCart(item: CartItem): void {
    console.log('Inside addToCart for item:', item.dish.name);
    const cartItems = [...this.cartItemsSignal()];
    // match existing item by dish id, spice level, and bulk metadata
    const existingItem = cartItems.find((ci) => {
      if (ci.dish._id !== item.dish._id) return false;
      if (ci.spiceLevel !== item.spiceLevel) return false;
      const a = JSON.stringify(ci.bulk || {});
      const b = JSON.stringify(item.bulk || {});
      return a === b;
    });
    if (existingItem) {
      existingItem.quantity += item.quantity;
    } else {
      cartItems.push(item);
    }
    this.cartItemsSignal.set(cartItems);
    this.journeyAnalytics.track('order', 'add_to_cart');
    // Now set the order signal with updated cart items and other details
    const order: Order = {
      cartItems: this.cartItemsSignal(),
      orderStatus: 'Pre Initiation',
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
      orderType: this.selectedOrderType(),
      orderDate: new Date(),
    };
    if (this.selectedDeliveryAddress) order.deliveryAddress = this.selectedDeliveryAddress;
    this.orderSignal.set(order);
    console.log('Setting order signal with order:', this.orderSignal());
  }

  varyItemQtyInCart(item: CartItem): void {
    const cartItems = [...this.cartItemsSignal()];
    const existingItemIndex = cartItems.findIndex((ci) => {
      if (ci.dish._id !== item.dish._id) return false;
      if (ci.spiceLevel !== item.spiceLevel) return false;
      const a = JSON.stringify(ci.bulk || {});
      const b = JSON.stringify(item.bulk || {});
      return a === b;
    });
    if (existingItemIndex > -1) {
      cartItems[existingItemIndex].quantity = item.quantity;
      if (cartItems[existingItemIndex].quantity <= 0) {
        cartItems.splice(existingItemIndex, 1);
      }
      this.cartItemsSignal.set(cartItems);
      const order: Order = {
        cartItems: this.cartItemsSignal(),
        orderStatus: 'Pre Initiation',
        // deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
        payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
        orderType: this.selectedOrderType(),
        orderDate: new Date(),
      };
      if (this.selectedDeliveryAddress) order.deliveryAddress = this.selectedDeliveryAddress;
      this.orderSignal.set(order);
      console.log('Updated cart item quantity. Updated order:', this.orderSignal());
      return;
    }

    // If the item wasn't present and the new quantity is > 0, add it to the cart.
    if (item.quantity > 0) {
      cartItems.push(item);
      this.cartItemsSignal.set(cartItems);
      const order: Order = {
        cartItems: this.cartItemsSignal(),
        orderStatus: 'Pre Initiation',
        // deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
        payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
        orderType: this.selectedOrderType(),
        orderDate: new Date(),
      };
      if (this.selectedDeliveryAddress) order.deliveryAddress = this.selectedDeliveryAddress;
      console.log('Added new cart item. Updated order:', order);
      this.orderSignal.set(order);
    }
  }

  setOrderStatus(status: OrderStatus): void {
    const currentOrder = this.orderSignal();
    if (!currentOrder) return;
    const updatedOrder: Order = {
      ...currentOrder,
      orderStatus: status,
    };
    this.orderSignal.set(updatedOrder);
  }

  varyItemSpiceLevelInCart(item: CartItem): void {
    const cartItems = [...this.cartItemsSignal()];
    // Try to find the exact cart item matching dish, bulk and quantity (best-effort to locate the right slot)
    const bulkKey = (b: CartItem) => JSON.stringify(b.bulk || {});
    let existingItemIndex = cartItems.findIndex((ci) => {
      if (ci.dish._id !== item.dish._id) return false;
      if (bulkKey(ci) !== bulkKey(item)) return false;
      if (ci.quantity === item.quantity) return true;
      return false;
    });

    // fallback: find first matching dish+bulk
    if (existingItemIndex === -1) {
      existingItemIndex = cartItems.findIndex(
        (ci) => ci.dish._id === item.dish._id && bulkKey(ci) === bulkKey(item),
      );
    }

    if (existingItemIndex > -1) {
      // If there's already an item with the target spice level, merge quantities
      const targetIndex = cartItems.findIndex(
        (ci) =>
          ci.dish._id === item.dish._id &&
          ci.spiceLevel === item.spiceLevel &&
          bulkKey(ci) === bulkKey(item),
      );
      if (targetIndex > -1 && targetIndex !== existingItemIndex) {
        cartItems[targetIndex].quantity += cartItems[existingItemIndex].quantity;
        cartItems.splice(existingItemIndex, 1);
      } else {
        cartItems[existingItemIndex].spiceLevel = item.spiceLevel;
      }
      this.cartItemsSignal.set(cartItems);
      const order: Order = {
        cartItems: this.cartItemsSignal(),
        orderStatus: 'Pre Initiation',
        // deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
        payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
        orderType: this.selectedOrderType(),
        orderDate: new Date(),
      };
      if (this.selectedDeliveryAddress) order.deliveryAddress = this.selectedDeliveryAddress;
      this.orderSignal.set(order);
      console.log('Updated cart item spice level. Updated order:', this.orderSignal());
    }
  }

  getCartItemsOriginalAndNetTotal(): [number, number] {
    const cartItems = this.cartItemsSignal();
    const lineUnit = (item: CartItem) =>
      item.bulk?.unitPrice != null ? item.bulk.unitPrice : item.dish.netPrice;
    const lineOriginal = (item: CartItem) =>
      item.bulk?.unitPrice != null ? item.bulk.unitPrice : item.dish.originalPrice;
    return [
      cartItems.reduce((total, item) => total + lineOriginal(item) * item.quantity, 0),
      cartItems.reduce((total, item) => total + lineUnit(item) * item.quantity, 0),
    ];
  }

  /**
   * Payable amount for order API payloads. When cover credit is applied at payment
   * (immediate dine-in with reservation), send gross food total; the backend deducts credit once.
   */
  getOrderPayableAmount(): number {
    const bill = this.getBillDetails();
    const party = this.reservationPartySizeSignal();
    const sessionCover = this.tableSessionRemainingCoverInr();
    const coverAppliedAtPayment =
      this.selectedOrderType() === 'Dine-In' &&
      !!this.reservationMongoIdSignal() &&
      (sessionCover != null || (party != null && party > 0));
    if (coverAppliedAtPayment) {
      return bill.finalTotalBeforeReservationCover;
    }
    return bill.finalTotal;
  }

  getBillDetails(): {
    originalTotal: number;
    netTotal: number;
    additionalDiscount: number;
    deliveryFee: number;
    gstAndOtherCharges: number;
    reservationCoverCredit: number;
    finalTotalBeforeReservationCover: number;
    finalTotal: number;
  } {
    const [originalTotal, netTotal] = this.getCartItemsOriginalAndNetTotal();
    const finalTotalBeforeReservationCover =
      netTotal -
      this.additionalDiscount +
      this.deliveryFee +
      this.otherCharges +
      this.gstRate * netTotal;
    const party = this.reservationPartySizeSignal();
    const sessionCover = this.tableSessionRemainingCoverInr();
    const reservationCoverEligible =
      this.selectedOrderType() === 'Dine-In' &&
      !!this.reservationMongoIdSignal() &&
      (sessionCover != null || (party != null && party > 0));
    const reservationCoverCredit = reservationCoverEligible
      ? this.resolveReservationCoverCreditInr(
          finalTotalBeforeReservationCover,
          sessionCover,
        )
      : 0;
    const finalTotal = this.roundPayableAmount(
      Math.max(0, finalTotalBeforeReservationCover - reservationCoverCredit),
    );
    return {
      originalTotal,
      netTotal,
      additionalDiscount: this.additionalDiscount,
      deliveryFee: this.deliveryFee,
      gstAndOtherCharges: this.gstRate * netTotal + this.otherCharges,
      reservationCoverCredit,
      finalTotalBeforeReservationCover: this.roundPayableAmount(finalTotalBeforeReservationCover),
      finalTotal,
    };
  }

  /** Sync cover balance from an active table session (null clears session balance). */
  applyTableSessionCoverBalance(remainingCoverInr: number | null | undefined): void {
    if (remainingCoverInr == null) {
      this.tableSessionRemainingCoverInr.set(null);
    } else {
      this.tableSessionRemainingCoverInr.set(
        this.roundPayableAmount(Math.max(0, remainingCoverInr)),
      );
    }
    this.refreshOrderPayableAmount();
  }

  private resolveReservationCoverCreditInr(
    grossBeforeCover: number,
    sessionCover: number | null = this.tableSessionRemainingCoverInr(),
  ): number {
    const balance = sessionCover ?? reservationCoverFoodCreditInr();
    return this.roundPayableAmount(
      Math.min(Math.max(0, balance), Math.max(0, grossBeforeCover)),
    );
  }

  private refreshOrderPayableAmount(): void {
    const current = this.orderSignal();
    if (!current) {
      return;
    }
    this.orderSignal.set({
      ...current,
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
    });
  }

  clearCart(): void {
    this.cartItemsSignal.set([]);
    this.reservationMongoIdSignal.set(null);
    this.reservationNumberSignal.set(null);
    this.reservationPartySizeSignal.set(null);
    this.tableSessionRemainingCoverInr.set(null);
    const order: Order = {
      cartItems: this.cartItemsSignal(),
      orderStatus: 'Pre Initiation',
      deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
      orderType: this.selectedOrderType(),
      orderDate: new Date(),
    };
    this.orderSignal.set(order);
  }

  /** Load cart lines from an existing order (e.g. pre-order → dine-in checkout). */
  replaceCartFromExistingOrder(order: Order): void {
    const items = (order.cartItems ?? []).map((ci) => ({
      ...ci,
      dish: {
        ...ci.dish,
        _id: ci.dish._id != null ? String(ci.dish._id) : ci.dish._id,
      },
    })) as CartItem[];
    this.cartItemsSignal.set(items);
    const next: Order = {
      cartItems: items,
      orderStatus: 'Pre Initiation',
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
      orderType: this.selectedOrderType(),
      orderDate: new Date(),
    };
    if (this.selectedDeliveryAddress) next.deliveryAddress = this.selectedDeliveryAddress;
    this.orderSignal.set(next);
  }

  addDeliveryAddress(address: Address): void {
    this.savedDeliveryAddresses.push(address);
    this.selectedDeliveryAddress = address;
    this.deliveryAddressSelectedSignal.set(true);
    const order: Order = {
      cartItems: this.cartItemsSignal(),
      orderStatus: 'Pre Initiation',
      deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
      orderType: this.selectedOrderType(),
      orderDate: new Date(),
    };
    this.orderSignal.set(order);
  }

  editDeliveryAddress(address: Address): void {
    this.savedDeliveryAddresses.splice(0, 1, address);
    this.selectedDeliveryAddress = address;
    this.deliveryAddressSelectedSignal.set(true);
    const order: Order = {
      cartItems: this.cartItemsSignal(),
      orderStatus: 'Pre Initiation',
      deliveryAddress: this.selectedDeliveryAddress, // This will be null for non-delivery orders, which is fine
      payableAmount: this.roundPayableAmount(this.getOrderPayableAmount()),
      orderType: this.selectedOrderType(),
      orderDate: new Date(),
    };
    this.orderSignal.set(order);
  }

  getSavedDeliveryAddresses(): Address[] {
    return this.savedDeliveryAddresses;
  }

  getSelectedDeliveryAddress(): Address | null {
    return this.selectedDeliveryAddress;
  }

  setAddressDialogOpen(v: boolean) {
    this.isAddressDialogOpen.set(!!v);
  }

  updateOrderPickupTime(pickupTime: Date) {
    const order = this.orderSignal();
    if (!order) return;

    this.orderSignal.set({
      ...order,
      pickupTime,
    });

    if (!order._id) return;
    const trackingJwt = this.trackingJwtService.readPersistedTrackingJwt();
    if (!trackingJwt) return;

    this.orderApi.updateOrderPickupTime(order._id, pickupTime, trackingJwt).subscribe((response) => {
      if (!response?.updatedOrder) return;
      this.orderSignal.set(response.updatedOrder);
    });
  }
}
